<template>
  <div>
    <!-- [{ changeOrderId: { category: [lineItems] } }] -->
    <div
      v-for="(changeOrder, index) in changeOrdersItems"
      :key="`changeOder-${index}`"
      class="mb-3 bg-warning-200 rounded-lg"
    >
      <!-- { changeOrderId: { category: [lineItems] } } -->
      <div
        v-for="(changeOderItems, changeOrderId) in changeOrder"
        :key="`changeOder-${changeOrderId}`"
      >
        <!-- { category: [lineItems] } -->
        <div
          v-for="(lineItems, category) in changeOderItems"
          :key="category"
        >
          <!-- actions -->
          <div class="border-b-2 border-warning-400 py-2">
            <div class="px-4 flex justify-between items-center">
              <span class="font-semibold text-xl mb-3">{{ category }}</span>

              <!-- actions dropdown -->
              <div class="flex gap-4">
                <!-- change order status badge -->
                <ProposalStatusBadge :status="getChangeOrderStatus(String(changeOrderId))" />

                <!-- actions dropdown -->
                <BaseDropdown
                  right
                  :items="[
                    { title: 'Edit', to:{ name: 'EditChangeOrder', params: { id: changeOrderId, proposalId: proposal.id }}, disabled: disablAccept(String(changeOrderId)) },
                    { title: 'Preview', to:{ name: 'CustomerViewChangeOrder', params: { id: changeOrderId, proposalId: proposal.id }, query: { preview: true } }, target: '_blank' },
                    { title: 'Email', action: () => emailChangeOrder(String(changeOrderId)) },
                    { title: 'Accept', action: () => acceptChangeOrder(String(changeOrderId)), disabled: disablAccept(String(changeOrderId)) },
                    { title: 'Decline', action: () => declineChangeOrder(String(changeOrderId)), disabled: disablAccept(String(changeOrderId)) ||disablDecline(String(changeOrderId)) },
                    { title: 'Undo Acceptance', action: () => undoAcceptance(String(changeOrderId)), disabled: disablAccept(String(changeOrderId)) || disablDecline(String(changeOrderId)) },
                    { title: 'Delete', action: () => showDeleteModal(String(changeOrderId)), disabled: disablDelete(String(changeOrderId)) },
                  ]"
                />
              </div>
            </div>
          </div>

          <!-- change order line items -->
          <div
            v-for="(lineItem, idx) in lineItems"
            :key="lineItem.id"
            :class="[
              { 'border-b border-warning-400': idx !== lineItems.length - 1 }
            ]"
          >
            <div class="p-2">
              <div class="grid grid-cols-5 gap-4">
                <div class="col-span-4 flex items-start">
                  <div v-if="lineItem.optional" class="mr-2 mt-4">
                    <!-- optional checkbox -->
                    <BaseCheckbox v-model="lineItem.selected" label="Optional" disabled />
                  </div>

                  <BaseCollapse :bg-color="'bg-withe'">
                    <template #header>
                      <div class="flex items-center">
                        <span class="font-bold">{{ lineItem.title }}</span>
                        <span v-if="lineItem.isCreditLineItem" class="ml-2">
                          <CreditBadge />
                        </span>
                      </div>
                    </template>

                    <div class="text-justify" v-html="lineItem.scopeOfWork" />

                    <!-- attachments -->
                    <LightBox :attachments="lineItem.attachments" />
                  </BaseCollapse>
                </div>

                <!-- totals -->
                <!-- accepted pricing infos -->
                <div
                  v-if="isAccecptedOrArchived(String(changeOrderId))"
                  class="flex flex-col gap-1 mt-4 text-sm"
                >
                  <p class="grid grid-cols-2">
                    <span class="font-semibold">Total Price:</span>
                    <span>{{ currency(lineItem.totalPrice) }}</span>
                  </p>
                  <p class="grid grid-cols-2">
                    <span class="font-semibold">Invoiced:</span>
                    <span>0%</span>
                  </p>
                  <p class="grid grid-cols-2">
                    <span class="font-semibold">Balance:</span>
                    <span>{{ currency(lineItem.totalPrice) }}</span>
                  </p>
                </div>
                <!-- Not accepted yet pricing infos -->
                <div
                  v-else
                  class="flex flex-col gap-1 mt-4 text-sm"
                >
                  <p class="grid grid-cols-2">
                    <span class="font-semibold">Total Price:</span>
                    <span>{{ currency(lineItem.totalPrice) }}</span>
                  </p>
                  <p class="grid grid-cols-2">
                    <span class="font-semibold">Quantity:</span>
                    <span>{{ lineItem.quantity }}</span>
                  </p>
                  <div class="flex flex-col justify-center">
                    <p class="grid grid-cols-2">
                      <span class="font-semibold">Cost:</span>
                      <span>{{ currency(lineItem.cost) }}</span>
                    </p>
                    <span class="block text-xs font-bold text-gray-700">Not visible to client</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- delete confirmation modal -->
  <Modal
    id="delete-change-order-confirmation-modal"
    v-model="showDeleteConfirmationModal"
    title="Delete Change Order"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
  >
    <template #body>
      <div class="mt-2">
        <p class="text-base mb-3">Are you sure you want to delete this change order?</p>
      </div>
    </template>

    <template #footer>
      <div class="bg-white py-3 flex px-6 justify-end gap-2">
        <BaseButton
          text="Cancel"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="showDeleteConfirmationModal = false"
        />

        <BaseButton
          variant="danger"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="deleteChangeOrder()"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Confirm</span>
            <span
              v-if="isDeleting"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>
    </template>
  </Modal>

  <!-- email change order -->
  <Modal
    id="email-change-order-modal"
    v-model="showEmailChangeOrderModal"
    title="Send Change Order"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    width="md:w-[46rem] w-full"
  >
    <ChangeOrderEmail
      :change-order="getChangeOrder(changeOrderToEmail)"
      :proposal="proposal"
      :email="(email as Email)"
      @close="showEmailChangeOrderModal = false"
    />
  </Modal>
</template>

<script setup lang="ts">
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import ProposalStatusBadge from '../components/ProposalStatusBadge.vue'
import { Budget, BudgetItem } from '@/models/budget'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import ProposalService from '@/services/ProposalService'
import { currency } from '@/utils'
import { notify } from '@kyvg/vue3-notification'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { extend, filter, groupBy } from 'underscore'
import { computed, PropType, ref } from 'vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseCollapse from '@/components/base/BaseCollapse.vue'
import BudgetService from '@/services/BudgetService'
import Modal from '@/components/app/AppModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ChangeOrderEmail from './ChangeOrderEmail.vue'
import { Project } from '@/models/project'
import { Customer } from '@/models/customer'
import { Email } from '@/models/email'
import Handlebars from 'handlebars'
import EmailService, { EmailTemplate } from '@/services/EmailService'
import { useAuth } from '@/composables/useAuth'
import LightBox from '@/components/app/AppLightBox.vue'
import CreditBadge from '@/components/base/CreditBadge.vue'

const props = defineProps({
  changeOrders: {
    type: Array as PropType<Proposal[]>,
    required: true
  },
  proposal: {
    type: Object as PropType<Proposal>,
    required: true
  },
  budget: {
    type: Object as PropType<Budget>,
    required: true
  },
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  customer: {
    type: Object as PropType<Customer>,
    required: true
  }
})


const proposalService = new ProposalService()
const budgetService = new BudgetService()
const queryClient = useQueryClient()
const showDeleteConfirmationModal = ref(false)
const changeOrderIdToDelete = ref('')
const showEmailChangeOrderModal = ref(false)
const changeOrderToEmail = ref('')

const getChangeOrderStatus = (changeOrderId: string) => {
  const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
  return changeOrder.status
}

const isAccecptedOrArchived = computed(() => (changeOrderId: string) => {
  const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
  return changeOrder.status === PROPOSAL_STATUS.ACCEPTED || changeOrder.status === PROPOSAL_STATUS.ARCHIVED
})

const changeOrdersItems = computed(() => {
  return props.changeOrders.map(changeOrder => {
    const items = props.budget.lineItems.filter(item => changeOrder.lineItems.includes(item.id)).sort((a, b) => {
      const categoryDiff = a.category.weight - b.category.weight
      return categoryDiff !== 0 ? categoryDiff : a.weight - b.weight
    })
    const changeOrderItems = groupBy(items, item => item.category.name)
    return { [changeOrder.id]: changeOrderItems }
  })
})

const { currentUser } = useAuth()

const { mutateAsync: acceptChangeOrder } = useMutation({
  mutationFn: async (changeOrderId: string) =>  {
    const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
    const updatedCHO = extend(new Proposal(), {...changeOrder, status: PROPOSAL_STATUS.ACCEPTED }) as Proposal
    updatedCHO.signature = {
      name: currentUser.value.name,
      timestamp: new Date()
    }

    return await proposalService.updateChangeOrder(props.proposal.id, changeOrderId, updatedCHO)
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['changeOrders', props.proposal.id] })

    notify({
      type: 'success',
      title: 'Change Order Accepted',
      text: 'Change Order accepted successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error accepting change order',
      text: error.message || 'An error occurred while accepting the change order'
    })
  }
})


const { mutateAsync: declineChangeOrder } = useMutation({
  mutationFn: async (changeOrderId: string) =>  {
    const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
    return await proposalService.updateChangeOrder(props.proposal.id, changeOrderId, {...changeOrder, status: PROPOSAL_STATUS.DECLINED })
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['changeOrders', props.proposal.id] })

    notify({
      type: 'success',
      title: 'Change Order Declined',
      text: 'Change Order declined successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error accepting change order',
      text: error.message || 'An error occurred while declining the change order'
    })
  }
})

const { mutateAsync: undoAcceptance } = useMutation({
  mutationFn: async (changeOrderId: string) =>  {
    const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)

    // update budget line items
    let updatedBudget = extend(new Budget(), props.budget) as Budget
    updatedBudget.lineItems = props.budget.lineItems.map(item => {
      if (changeOrder.lineItems.includes(item.id)) {
        return extend(new BudgetItem(), {
          ...item,
          selected: true
        })
      }
      return item
    })

    updatedBudget = await budgetService.updateBudget(updatedBudget, [])
    queryClient.setQueryData(['budget', updatedBudget.id], updatedBudget)

    return await proposalService.updateChangeOrder(props.proposal.id, changeOrderId, {...changeOrder, status: PROPOSAL_STATUS.DRAFT })
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['changeOrders', props.proposal.id] })

    notify({
      type: 'success',
      title: 'Change Order Acceptance Undone',
      text: 'Change Order acceptance undone successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error undo acceptance',
      text: error.message || 'An error occurred while undoing the acceptance'
    })
  }
})

const showDeleteModal = (changeOrderId: string) => {
  changeOrderIdToDelete.value = changeOrderId
  showDeleteConfirmationModal.value = true
}

const { mutateAsync: deleteChangeOrder, isPending: isDeleting } = useMutation({
  mutationFn: async () => {
    const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderIdToDelete.value)

    // update budget line items
    let updatedBudget = extend(new Budget(), props.budget) as Budget
    updatedBudget.lineItems = filter(props.budget.lineItems, item => !changeOrder.lineItems.includes(item.id))

    await proposalService.deleteChangeOrder(props.proposal.id, changeOrderIdToDelete.value)
    queryClient.invalidateQueries({ queryKey: ['changeOrders', props.proposal.id] })

    updatedBudget = await budgetService.updateBudget(updatedBudget, changeOrder.lineItems)
    queryClient.setQueryData(['budget', updatedBudget.id], updatedBudget)
  },
  onSuccess: () => {
    changeOrderIdToDelete.value = ''
    showDeleteConfirmationModal.value = false
    notify({
      type: 'success',
      title: 'Change Order Deleted',
      text: 'Change Order deleted successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error deleting change order',
      text: error.message || 'An error occurred while deleting the change order'
    })
  }
})


const disablDelete = computed(() => (changeOrderId: string) => {
  const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
  return changeOrder.status === PROPOSAL_STATUS.ACCEPTED || changeOrder.status === PROPOSAL_STATUS.ARCHIVED
})

const disablAccept = computed(() => (changeOrderId: string) => {
  const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
  return changeOrder.status === PROPOSAL_STATUS.ACCEPTED || changeOrder.status === PROPOSAL_STATUS.ARCHIVED
})

const disablDecline = computed(() => (changeOrderId: string) => {
  const changeOrder = props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
  return changeOrder.status === PROPOSAL_STATUS.DECLINED || changeOrder.status === PROPOSAL_STATUS.ARCHIVED
})

const getChangeOrder = computed(() => (changeOrderId: string) => {
  return props.changeOrders.find(changeOrder => changeOrder.id === changeOrderId)
})

const emailChangeOrder = (changeOrderId: string) => {
  changeOrderToEmail.value = changeOrderId

  const changeOrderLink = `${window.location.origin}/changeOrder/${changeOrderId}/${props.proposal.id}`
  const emailData = { customerName: props.customer.name, link: changeOrderLink }

  email.value.to = [props.customer.email]
  email.value.message.subject = Handlebars.compile(sendChangeOrder.value.subject).call(this, { projectName: props.project.name })
  Handlebars.registerPartial(baseLayout.value.name, baseLayout.value.html)
  email.value.message.html = Handlebars.compile(sendChangeOrder.value.html).call(this, emailData)

  showEmailChangeOrderModal.value = true
}

const email = ref({...new Email(), to: [], cc: []})
const emailService = new EmailService()

const { data: baseLayout } = useQuery({
  queryKey: ['emailTemplates/base_layout'],
  queryFn: async () => {
    return await emailService.find('base_layout') || new EmailTemplate()
  }
})

const { data: sendChangeOrder } = useQuery({
  queryKey: ['emailTemplates/send_change_order'],
  queryFn: async () => {
    return await emailService.find('send_change_order') || new EmailTemplate()
  }
})

</script>
